import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { PageHeader, TableShell, Field, Drawer } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Download, Plus, Trash2, GripVertical, FileSpreadsheet, Eye } from "lucide-react";

const STATUSES = ["pending", "assigned", "in_progress", "completed", "canceled"];
const fmtDeletedDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

const empty = {
  name: "",
  description: "",
  columns: [],
  default_days: 30,
  include_statuses: ["completed"],
};

function GenerateModal({ template, companies, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const defaultStart = new Date(Date.now() - (template?.default_days || 30) * 86400000)
    .toISOString()
    .slice(0, 10);
  const [dateFrom, setDateFrom] = useState(defaultStart);
  const [dateTo, setDateTo] = useState(today);
  const [companyId, setCompanyId] = useState("");
  const [statuses, setStatuses] = useState(template?.include_statuses || []);
  const [busy, setBusy] = useState(false);

  const download = () => {
    const token = localStorage.getItem("nemt_token");
    const qs = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
      auth: token || "",
    });
    if (companyId) qs.set("company_id", companyId);
    if (statuses.length) qs.set("statuses", statuses.join(","));
    setBusy(true);
    window.location.href = `${API_BASE}/exports/templates/${template.id}/generate?${qs.toString()}`;
    setTimeout(() => { setBusy(false); onClose(); }, 800);
  };

  const toggleStatus = (s) => setStatuses((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);

  if (!template) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="generate-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="font-display font-semibold text-lg text-[#0A192F] mb-1">Generate CSV</div>
        <div className="text-xs text-slate-500 mb-4">{template.name} — {template.columns.length} columns</div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="From" testId="gen-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <Field label="To" testId="gen-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <Field
            label="Client company"
            testId="gen-company"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            options={[{ value: "", label: "All companies" }, ...companies.map((c) => ({ value: c.id, label: c.name }))]}
          />
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 block mb-1.5">
              Statuses ({statuses.length || "all"})
            </label>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => toggleStatus(s)}
                  className={`text-xs px-3 py-1 rounded-full border transition ${statuses.includes(s) ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  data-testid={`gen-status-${s}`}
                >
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
          <button onClick={download} disabled={busy} className="btn-primary flex items-center gap-1.5" data-testid="download-csv-btn">
            <Download size={14} /> {busy ? "Downloading…" : "Download CSV"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ColumnEditor({ columns, setColumns, fields }) {
  const fieldOptions = useMemo(
    () => [{ value: "", label: "— select field —" }, ...fields.map((f) => ({ value: f.key, label: `${f.category} · ${f.label}` }))],
    [fields]
  );

  const update = (idx, key, val) => {
    setColumns((cols) => cols.map((c, i) => (i === idx ? { ...c, [key]: val } : c)));
  };
  const remove = (idx) => setColumns((cols) => cols.filter((_, i) => i !== idx));
  const add = () => setColumns((cols) => [...cols, { header: "", source: "" }]);
  const move = (idx, dir) => {
    setColumns((cols) => {
      const next = [...cols];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return cols;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };
  const suggestHeader = (idx) => {
    const src = columns[idx]?.source;
    const field = fields.find((f) => f.key === src);
    if (field && !columns[idx].header) update(idx, "header", field.label);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Columns ({columns.length})</div>
        <button type="button" onClick={add} className="text-xs px-2.5 py-1 border border-slate-200 rounded-md hover:border-slate-400 inline-flex items-center gap-1" data-testid="add-column-btn">
          <Plus size={12} /> Add column
        </button>
      </div>
      {columns.length === 0 && (
        <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-md p-4 text-center">
          No columns yet. Click Add column to start mapping ride fields to your CSV headers.
        </div>
      )}
      <div className="space-y-2">
        {columns.map((c, idx) => (
          <div key={idx} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-2" data-testid={`col-row-${idx}`}>
            <div className="flex flex-col text-slate-400">
              <button type="button" onClick={() => move(idx, -1)} className="text-[10px] leading-none hover:text-slate-700" data-testid={`move-up-${idx}`}>▲</button>
              <button type="button" onClick={() => move(idx, 1)} className="text-[10px] leading-none hover:text-slate-700" data-testid={`move-down-${idx}`}>▼</button>
            </div>
            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={c.header}
                onChange={(e) => update(idx, "header", e.target.value)}
                placeholder="CSV header"
                className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid={`col-header-${idx}`}
              />
            </div>
            <div className="w-1 text-slate-300">→</div>
            <div className="flex-1 min-w-0">
              <select
                value={c.source}
                onChange={(e) => { update(idx, "source", e.target.value); }}
                onBlur={() => suggestHeader(idx)}
                className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid={`col-source-${idx}`}
              >
                {fieldOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <button type="button" onClick={() => remove(idx)} className="text-slate-400 hover:text-rose-600 p-1" data-testid={`remove-col-${idx}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ExportTemplates() {
  const { user } = useAuth();
  const isAdmin = user?.role === "agency_admin";
  const [rows, setRows] = useState([]);
  const [fields, setFields] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generateTpl, setGenerateTpl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const load = () => api.get(`/exports/templates${showDeleted ? "?include_deleted=true" : ""}`).then((r) => setRows(r.data));

  useEffect(() => {
    load();
    api.get("/exports/fields").then((r) => setFields(r.data.fields));
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  const restore = async (id) => {
    try {
      await api.post(`/exports/templates/${id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = () => {
    setForm({ ...empty, columns: [
      { header: "Patient", source: "patient_name" },
      { header: "Pickup Date", source: "pickup_date" },
      { header: "Pickup Address", source: "pickup_address" },
      { header: "Dropoff Address", source: "dropoff_address" },
      { header: "Driver", source: "driver_name" },
      { header: "Miles", source: "billable_miles" },
      { header: "Fare", source: "fare" },
    ] });
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (t) => {
    setForm({
      name: t.name,
      description: t.description || "",
      columns: t.columns.map((c) => ({ ...c })),
      default_days: t.default_days || 30,
      include_statuses: t.include_statuses || [],
    });
    setEditingId(t.id);
    setOpen(true);
  };

  const setCols = (updater) => {
    setForm((f) => ({ ...f, columns: typeof updater === "function" ? updater(f.columns) : updater }));
  };

  const toggleStatus = (s) => {
    setForm((f) => ({
      ...f,
      include_statuses: f.include_statuses.includes(s)
        ? f.include_statuses.filter((x) => x !== s)
        : [...f.include_statuses, s],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name is required");
    if (!form.columns.length) return toast.error("Add at least one column");
    for (const c of form.columns) {
      if (!c.header?.trim() || !c.source) return toast.error("Every column needs a header and a mapped field");
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        columns: form.columns.map((c) => ({ header: c.header.trim(), source: c.source })),
        default_days: Number(form.default_days) || 30,
        include_statuses: form.include_statuses.length ? form.include_statuses : null,
      };
      if (editingId) await api.put(`/exports/templates/${editingId}`, payload);
      else await api.post("/exports/templates", payload);
      toast.success("Template saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This can be restored by an admin from Show deleted.`)) return;
    try {
      await api.delete(`/exports/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const runPreview = async () => {
    if (!form.columns.length) return toast.error("Add at least one column first");
    for (const c of form.columns) {
      if (!c.header?.trim() || !c.source) return toast.error("Every column needs a header and a mapped field");
    }
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const { data } = await api.post("/exports/preview", {
        columns: form.columns.map((c) => ({ header: c.header.trim(), source: c.source })),
        include_statuses: form.include_statuses.length ? form.include_statuses : null,
        limit: 20,
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div data-testid="export-templates-page">
      <PageHeader
        subtitle="Data Export"
        title="Export Templates"
        action={
          <div className="flex items-center gap-4">
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} data-testid="export-templates-show-deleted" />
                Show deleted
              </label>
            )}
            <button onClick={openNew} className="btn-primary flex items-center gap-1.5" data-testid="new-template-btn">
              <Plus size={14} /> New Template
            </button>
          </div>
        }
      />

      <div className="text-sm text-slate-600 mb-4 max-w-3xl">
        Build custom CSV formats that map ride data to the exact column headers your other dispatch or payroll app expects.
        Templates run over a date range and download instantly with one click.
      </div>

      <TableShell
        columns={["Name", "Description", "Columns", "Default range", "Statuses", "Actions"]}
        empty={rows.length === 0 ? "No templates yet — click New Template to build your first custom CSV." : null}
      >
        {rows.map((t) => {
          const isDeleted = !!t.deleted_at;
          return (
            <tr key={t.id} className={`tbl-row border-t border-slate-100 ${isDeleted ? "opacity-60" : ""}`} data-testid={`tpl-row-${t.id}`}>
              <td className="px-4 py-2.5 font-medium text-slate-900">
                <div className="inline-flex items-center gap-2">
                  <FileSpreadsheet size={14} className="text-emerald-600" />{t.name}
                </div>
                {isDeleted && (
                  <span className="ml-2 inline-block bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 text-[10px] align-middle">
                    deleted {fmtDeletedDate(t.deleted_at)}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 text-slate-600 max-w-[260px] truncate">{t.description || <span className="text-slate-400">-</span>}</td>
              <td className="px-4 py-2.5 text-slate-600">{t.columns.length}</td>
              <td className="px-4 py-2.5 text-slate-600">{t.default_days} days</td>
              <td className="px-4 py-2.5 text-slate-600 text-xs">
                {t.include_statuses?.length ? t.include_statuses.join(", ").replace(/_/g, " ") : "all"}
              </td>
              <td className="px-4 py-2.5 space-x-3 whitespace-nowrap">
                {isDeleted ? (
                  <button className="text-xs text-emerald-700 hover:underline" onClick={() => restore(t.id)} data-testid={`restore-${t.id}`}>Restore</button>
                ) : (
                  <>
                    <button className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1" onClick={() => setGenerateTpl(t)} data-testid={`generate-${t.id}`}>
                      <Download size={12} /> Generate
                    </button>
                    <button className="text-xs text-[#005E82] hover:underline" onClick={() => openEdit(t)} data-testid={`edit-${t.id}`}>Edit</button>
                    <button className="text-xs text-rose-600 hover:underline" onClick={() => remove(t)} data-testid={`delete-${t.id}`}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </TableShell>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? "Edit Template" : "New Template"}
        footer={
          <>
            <button onClick={runPreview} className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400 inline-flex items-center gap-1.5" data-testid="preview-btn">
              <Eye size={13} /> Preview
            </button>
            <button onClick={() => setOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-template-btn">
              {saving ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Template name" testId="tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Payroll — Weekly Driver Miles" />
          <Field label="Description (optional)" testId="tpl-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What this export is for" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Default date range (days)" testId="tpl-days" type="number" min="1" max="365" value={form.default_days} onChange={(e) => setForm({ ...form, default_days: e.target.value })} />
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 block mb-1">Statuses to include</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleStatus(s)}
                    className={`text-[11px] px-2 py-1 rounded-full border ${form.include_statuses.includes(s) ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200"}`}
                    data-testid={`tpl-status-${s}`}
                  >
                    {s.replace("_", " ")}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-slate-400 mt-1">Empty = all statuses</div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <ColumnEditor columns={form.columns} setColumns={setCols} fields={fields} />
          </div>
        </div>
      </Drawer>

      {generateTpl && (
        <GenerateModal
          template={generateTpl}
          companies={companies}
          onClose={() => setGenerateTpl(null)}
        />
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="preview-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="font-display font-semibold text-slate-900">Preview</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {previewLoading ? "Loading…" : preview ? `${preview.returned} of ${preview.total_matched} matching rides (${preview.date_from} → ${preview.date_to})` : ""}
                </div>
              </div>
              <button onClick={() => { setPreviewOpen(false); setPreview(null); }} className="text-slate-500 text-sm hover:text-slate-900" data-testid="preview-close">Close</button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {previewLoading && <div className="text-sm text-slate-500 text-center py-8">Loading preview…</div>}
              {preview && preview.rows.length === 0 && (
                <div className="text-sm text-slate-500 text-center py-8">No rides match. Widen the date range or statuses.</div>
              )}
              {preview && preview.rows.length > 0 && (
                <table className="w-full text-xs border border-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      {preview.headers.map((h) => (
                        <th key={h} className="text-left px-2 py-1.5 border-b border-slate-200 font-semibold text-slate-700">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        {preview.headers.map((h) => (
                          <td key={h} className="px-2 py-1.5 text-slate-700 max-w-[220px] truncate" title={r[h]}>{r[h]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
